import { OnDestroy } from '@angular/core';
import { applyTransaction } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ErrorHandlerService } from '@y42/error-handling';
import { AbstractFeatureStoreModel } from '@y42/feature';
import { Y42GitFolders, Y42GitPath } from '@y42/feature/git';
import {
  AbstractGitEntityState,
  AbstractGitEntityStore,
  AbstractStoreModelWithChildren,
  AbstractStoreModelWithParents,
} from '@y42/feature/git/state';
import { Y42MetadataGitModel } from '@y42/feature/json-schema';
import { Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { GitQuery } from '@y42/git';
import { JobStatus, TriggeredByType, Y42FeatureType } from '@y42/json-schema';
import { waitFor } from '@y42/rxjs';
import { isNotNullish } from '@y42/util/nullish';
import { Optional } from 'ag-grid-community';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  DefaultJobSearchFilter,
  FinalJobStatuses,
  JobHttpModel,
  JobParentType,
  JobQuery,
  JobSortFilter,
  JobState,
  JobStatusCode,
  JobStore,
  JobStoreModel,
  JobUIModel,
} from '../state';
import { AbstractJobCancelationService, JobCancelationService } from './services/job-cancelation.service';
import { JobContextService } from './services/job-context.service';
import { AllJobsRequestOptions, JobHttpService } from './services/job-http/job-http.service';
import { JobTriggerService } from './services/job-trigger.service';

export interface TableStoreModel
  extends Y42MetadataGitModel,
    AbstractStoreModelWithChildren,
    AbstractStoreModelWithParents {}

export interface JobUpdateDetails {
  id: string;
  path: string;
  status: JobStatus;
  import_status: JobStatus;
}

export interface JobOriginatorData {
  orgId: JobHttpModel['request']['context']['company_slug'];
  orgSlug: JobHttpModel['request']['context']['company_slug'];
  userId: JobHttpModel['request']['context']['triggered_by_id'];
  branch: JobHttpModel['request']['storage']['branch'];
}

export interface RefreshJobsOptions {
  paths?: string[];
  exactLocation?: boolean;
  limit?: number;
  force?: boolean;
}

@UntilDestroy()
export abstract class AbstractJobService<
  HttpModel extends JobHttpModel = JobHttpModel,
  UIModel extends JobUIModel = JobUIModel,
> implements AbstractJobCancelationService, OnDestroy
{
  constructor(
    protected readonly jobTriggerService: JobTriggerService,
    protected readonly parentStore: AbstractGitEntityStore<AbstractGitEntityState<TableStoreModel>>,
    protected readonly jobStore: JobStore<JobState>,
    protected readonly jobQuery: JobQuery<JobState, UIModel>,
    protected readonly jobContext: JobContextService,
    protected readonly gitQuery: GitQuery,
    protected readonly errorHandlerService: ErrorHandlerService,
    protected readonly jobHttpService: JobHttpService<HttpModel>,
    protected readonly gitRootPath: Y42GitPath<Y42GitFolders>,
    protected readonly featureRoute: Y42Routes,
    protected readonly routerService: Y42RouterService,
    protected readonly grandParentLoading$: Observable<boolean>,
    protected readonly grandParentId$: Observable<AbstractFeatureStoreModel['$path'] | null | undefined>,

    @Optional() jobCancelationService?: AbstractJobCancelationService,
  ) {
    this.jobCancelationService =
      jobCancelationService ||
      new JobCancelationService(this.jobQuery, this.gitQuery, this.jobTriggerService, this.errorHandlerService);

    combineLatest([this.jobQuery.dateRange$.pipe(debounceTime(500)), this.refreshTrigger$])
      .pipe(
        tap(() => {
          this.jobsLoading$$.next(true);
        }),
        withLatestFrom(grandParentId$),
        waitFor(
          combineLatest([grandParentLoading$, jobQuery.loading$, gitQuery.loading$]).pipe(
            map(([grandParentLoading, jobLoading, gitLoading]) => grandParentLoading && jobLoading && gitLoading),
            filter((loading) => !loading),
          ),
        ),
        switchMap(async ([[dateRange, options], path]) => {
          const exactLocation = options.paths ? options.exactLocation : false;
          const paths = options.paths ?? [path || gitRootPath];
          try {
            await this.getAllJobs(paths, { dateRange, limit: options.limit, force: options.force, exactLocation });
          } catch (error) {
            this.errorHandlerService.error('[AbstractJobService.constructor]: getAllJobs call failed', {
              toast: 'Could not retrieve jobs. Please, try again later.',
              context: {
                error,
                paths,
                dateRange,
              },
            });
          }
        }),
        tap(() => {
          this.jobsLoading$$.next(false);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private readonly jobCancelationService: AbstractJobCancelationService;

  private readonly jobsLoading$$ = new BehaviorSubject(false);
  readonly jobsLoading$: Observable<boolean> = this.jobsLoading$$;
  private readonly refreshTrigger$ = new Subject<RefreshJobsOptions>();

  abstract readonly featureType: Y42FeatureType;

  abstract readonly parentType: JobParentType;

  abstract readonly defaultFeatureGitPath: string;

  private readonly sortFilter$: Observable<JobSortFilter | undefined> = combineLatest([
    this.jobQuery.sortFilter$,
    this.jobContext.context$,
  ]).pipe(map(([sortFilter, context]) => sortFilter[context]));

  readonly loading$ = this.jobQuery.loading$;
  readonly jobs$ = this.jobQuery.jobs$;
  readonly maxDateRange: number = this.jobStore.maxDateRange;
  readonly dateRange$ = this.jobQuery.dateRange$;
  readonly selectedDateRangeForm = this.jobQuery.selectedDateRangeForm;

  readonly searchKey$: Observable<string> = this.sortFilter$.pipe(
    map((sortFilter) => sortFilter?.searchKey ?? DefaultJobSearchFilter.searchKey),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly statusFilter$: Observable<JobStatusCode | null> = this.sortFilter$.pipe(
    map((sortFilter) => sortFilter?.statusFilter ?? DefaultJobSearchFilter.statusFilter),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  ngOnDestroy(): void {
    // Placeholder for proper typing
  }

  async getJob(id: JobStoreModel['request_id'], path: JobStoreModel['path']) {
    try {
      const job = await this.jobHttpService.getJob(id, path);
      this.upsertJobs([job]);
      return job;
    } catch (err) {
      this.errorHandlerService.handleError(err);
      return undefined;
    }
  }

  async fetchJobs(path: string, options: { limit?: number } = {}) {
    this.jobStore.setLoading(true);
    try {
      const jobs = await (await this.jobHttpService.getAllJobs([path], { ...options, exactLocation: true })).jobs;
      this.upsertJobs(jobs);

      return jobs;
    } catch (err) {
      this.errorHandlerService.handleError(err);
      return [];
    } finally {
      this.jobStore.setLoading(false);
    }
  }

  async getLatestJobs(options: { featureOrFeatureChildPaths?: string[] } = {}) {
    const featureOrFeatureChildPaths = options.featureOrFeatureChildPaths ?? [this.defaultFeatureGitPath];
    this.jobStore.setLoading(true);
    try {
      const [latestJobs, latestValidJobs] = await Promise.all([
        this.jobHttpService.getLatestJobs(featureOrFeatureChildPaths),
        this.jobHttpService.getLatestValidJobs(featureOrFeatureChildPaths),
      ]);

      const jobsMap = new Map([...latestJobs, ...latestValidJobs].map((job) => [job.request_id, job]));
      const jobs = [...jobsMap.values()];

      this.upsertJobs(jobs);

      return jobs;
    } catch (err) {
      this.errorHandlerService.handleError(err);
      return [];
    } finally {
      this.jobStore.setLoading(false);
    }
  }

  triggerJobs(
    paths: Array<JobStoreModel['path']>,
    config: {
      isIncremental: boolean;
    } & JobOriginatorData,
  ) {
    return this.jobTriggerService.triggerJobs(config.isIncremental, paths).pipe(
      map((response) => {
        if (response.jobs.length > 0) {
          this.upsertJobs(
            response.jobs.map((job) =>
              this.generateNewJob(job.path, {
                requestId: job.job_id,
                ...config,
              }),
            ),
          );
        }

        return response;
      }),
    );
  }

  refreshJobs(options: RefreshJobsOptions = {}) {
    this.refreshTrigger$.next(options);
  }

  cancelJob(id: JobStoreModel['request_id'], path: JobStoreModel['path']): Observable<void> {
    return this.jobCancelationService.cancelJob(id, path);
  }

  selectIsJobCanceling(id: JobStoreModel['request_id']): Observable<boolean> {
    return this.jobCancelationService.selectIsJobCanceling(id);
  }

  setActive(id: string | null) {
    this.jobStore.setActive(id);
  }

  setSearchKey(searchKey: string) {
    this.jobStore.setSearchKey(searchKey, this.jobContext.context);
  }

  setStatusFilter(statusFilter: JobStatusCode) {
    this.jobStore.setStatusFilter(statusFilter, this.jobContext.context);
  }

  async handleJobUpdate(job: JobUpdateDetails) {
    const existingJob = this.jobQuery.getEntity(job.id);
    // No need to call API for non-final job status changes.
    // Updating status property of a job in the store is enough.
    if (existingJob && !FinalJobStatuses.includes(job.status)) {
      this.upsertJobs([
        {
          ...existingJob,
          status: job.status,
          import_status: job.import_status,
        },
      ]);
      return;
    }

    const updateJobPromise = this.jobHttpService.getJob(job.id, job.path);
    const lastValidJobPromise =
      job.status === JobStatus.Ready
        ? this.jobHttpService.getLatestValidJobs([job.path], { exactLocation: true })
        : Promise.resolve<JobHttpModel[]>([]);

    try {
      const [affectedJob, [latestValidJob]] = await Promise.all([updateJobPromise, lastValidJobPromise]);
      const affectedJobIsLatestValidJob = latestValidJob && latestValidJob.request_id === affectedJob?.request_id;
      const jobsToUpsert = affectedJobIsLatestValidJob
        ? [latestValidJob]
        : [affectedJob, latestValidJob].filter(isNotNullish);
      this.upsertJobs(jobsToUpsert);
    } catch (error) {
      this.errorHandlerService.error('AbstractJobService.handleJobUpdate: failed to fetch job data.', {
        context: { error: (error as Error)?.message, job },
      });
    }
  }

  hasJob(id: JobStoreModel['request_id']): boolean {
    return this.jobQuery.hasEntity(id);
  }

  private async getAllJobs(featureOrFeatureChildPaths: string[], options?: AllJobsRequestOptions) {
    this.jobStore.setLoading(true);

    try {
      const { jobs, cursor, fromCache } = await this.jobHttpService.getAllJobs(featureOrFeatureChildPaths, options);
      console.debug(
        `AbstractJobService.getAllJobs: Received ${jobs.length} jobs for "${featureOrFeatureChildPaths.join(', ')}"`,
        jobs,
      );
      if (!fromCache) {
        this.upsertJobs(jobs);
      }
      this.jobStore.setLastFetched();
      return { jobs, cursor };
    } catch (err) {
      this.errorHandlerService.handleError(err);
      return { jobs: [] };
    } finally {
      this.jobStore.setLoading(false);
    }
  }

  private upsertJobs(jobs: JobHttpModel[]) {
    applyTransaction(() => {
      // 1. Upsert jobs into store
      this.jobStore.upsertMany(jobs);

      // 2. Update jobs' parents `$children` property
      this.parentStore.setSkipSync(true);
      const jobIdsByParentPath = new Map<string, Array<JobStoreModel['request_id']>>();

      jobs
        .filter((job) => job.path)
        .forEach((job) => {
          const jobsByPath = jobIdsByParentPath.get(job.path) ?? [];
          jobsByPath.push(job.request_id);
          jobIdsByParentPath.set(job.path, jobsByPath);
        });

      jobIdsByParentPath.forEach(($children, parentPath) =>
        this.parentStore.update(parentPath, (parent) => ({
          $children: Array.from(new Set([...parent.$children, ...$children])),
        })),
      );
      this.parentStore.setSkipSync(false);
    });
  }

  private generateNewJob(
    path: string,
    config: {
      requestId: string;
    } & JobOriginatorData,
  ): JobHttpModel {
    const job_status = JobStatus.New;
    const created_at = new Date().toISOString();

    return {
      request_id: config.requestId,
      path,
      company_id: config.orgId,
      created_at,
      details: {},
      request: {
        job_id: config.requestId,
        created_at,
        target: {
          info: {},
        },
        context: {
          company_id: config.orgId,
          company_slug: config.orgSlug,
          triggered_by_type: TriggeredByType.User,
          triggered_by_id: config.userId,
          trial: false,
        },
        table_settings: {},
        integration_secret_id: '',
        storage: {
          branch: config.branch,
          commit_sha: '',
          company_id: config.orgId,
          id: config.requestId,
          path,
        },
      },
      status: job_status,
      import_status: job_status,
      parent_job_id: config.requestId,
    };
  }
}
